/**
 * MicroTransactionDetail — the payload region revealed when an event row is
 * expanded in MicroTransactionsPanel (accordion).
 *
 * The row itself already shows the title, time, and size, so this renders only
 * the secondary metadata (process, doc path) plus the raw payload. It mounts
 * only while its row is expanded, so the raw_payload fetch is lazy by
 * construction. Loading, the backend's 413 "too large" refusal, errors, and
 * empty payloads are all handled. Styling uses the app's semantic tokens.
 */

import { Check, Copy, FileWarning, Inbox } from "lucide-react";
import { useCopyToClipboard } from "usehooks-ts";
import { Button } from "@/shared/components/ui/button";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { useMicroRawPayload } from "../hooks/useMicroRawPayload";
import type { MicroSummary } from "../hooks/useMicroTransactions";
import { JsonView } from "./JsonView";

function isEmptyPayload(payload: unknown): boolean {
  if (payload === null || payload === undefined) return true;
  if (typeof payload === "object") return Object.keys(payload).length === 0;
  return false;
}

// The raw_payload schema is stable (~18 fields, two long text blocks that
// JsonView clamps to 3 lines), so the loading skeleton mirrors that shape and
// structure to land near the expanded content's height — keeping the row's
// open animation from snapping when the payload arrives.
const PAYLOAD_SKELETON_ROWS = Array.from({ length: 18 }, (_, i) => `row-${i}`);
const PAYLOAD_SKELETON_TALL = new Set([6, 15]);
const PAYLOAD_SKELETON_WIDTHS = ["w-24", "w-40", "w-12", "w-3/4", "w-16", "w-8"];

export function MicroTransactionDetail({ micro }: { micro: MicroSummary }) {
  const { payload, isLoading, isTooLarge, error } = useMicroRawPayload(micro.id);
  const [copiedText, copy] = useCopyToClipboard();
  const copied = copiedText !== null;

  const showPayload = !isLoading && !isTooLarge && !error;
  const empty = showPayload && isEmptyPayload(payload);
  const canCopy = showPayload && !empty;

  const onCopy = () => copy(JSON.stringify(payload, null, 2));

  return (
    <div className="border-t bg-muted/20 px-4 py-3">
      <div className="mb-2.5 space-y-0.5 font-mono text-xs text-muted-foreground">
        <div className="truncate">{micro.process_name}</div>
        {micro.doc_path && <div className="break-all">{micro.doc_path}</div>}
      </div>

      <div className="mb-2 flex min-h-9 items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Raw payload
        </span>
        {canCopy && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCopy}
            aria-label="Copy payload"
          >
            {copied ? <Check /> : <Copy />}
            {copied ? "Copied" : "Copy"}
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="font-mono text-xs leading-relaxed">
          <Skeleton className="my-0.75 h-4 w-12" />
          <div className="ml-1.25 border-l pl-3">
            {PAYLOAD_SKELETON_ROWS.map((id, i) => (
              <div key={id} className="flex gap-2 py-0.75 pl-4">
                <Skeleton className="h-4 w-20 shrink-0" />
                {PAYLOAD_SKELETON_TALL.has(i) ? (
                  <div className="min-w-0 flex-1 space-y-1">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-4/5" />
                    <Skeleton className="mt-1 h-4 w-20" />
                  </div>
                ) : (
                  <Skeleton
                    className={`h-4 ${PAYLOAD_SKELETON_WIDTHS[i % PAYLOAD_SKELETON_WIDTHS.length]}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {isTooLarge && (
        <div className="flex items-start gap-2.5 rounded-lg border border-warning/30 bg-warning/10 px-3.5 py-3 text-sm text-foreground">
          <FileWarning className="size-4 shrink-0 mt-0.5 text-warning" />
          <span>
            Payload too large to display (over 2&nbsp;MB). The capture was recorded but isn't shown
            here.
          </span>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3.5 py-3 text-sm text-destructive">
          Failed to load payload. {error}
        </div>
      )}

      {empty && (
        <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
          <Inbox className="size-4" />
          No payload data.
        </div>
      )}

      {canCopy && (
        <div className="font-mono text-xs leading-relaxed text-foreground">
          <JsonView value={payload} />
        </div>
      )}
    </div>
  );
}
